/* oxlint-disable react/only-export-components -- context file intentionally
   exports a provider component alongside its hook and types. */
import { useTenantId } from "hooks/useTenant";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";

import {
  readScoutPopoverSession,
  writeScoutPopoverSession,
  type ScoutPopoverViewMode,
} from "./scoutPopoverSessionStorage";

export type ScoutPopoverSession = {
  conversationId: string | undefined;
  setConversationId: (id: string | undefined) => void;
  openedAt: Date;
  /** Clear the conversation and close the popover. */
  reset: () => void;
  /** Start a fresh conversation without closing the popover. */
  newChat: () => void;
  /** Publish the resolved conversation title into the shared session. */
  setTitle: (title: string | undefined) => void;
};

/**
 * Shared, sessionStorage-backed session for the top-nav ("big Scout") popover.
 *
 * This is a single instance shared across the whole customer layout so it can
 * be:
 *   - read by the top-nav trigger (which owns the visible popover + its view
 *     mode),
 *   - read by the full Scout page (to close the popover on arrival and offer a
 *     "resume conversation" chip),
 *   - written by in-chat links (to hand a full-page conversation off to the
 *     popover on in-app navigation).
 *
 * Persisting to storage means a refresh rehydrates the popover in its prior
 * view with the same conversation (see scoutPopoverSessionStorage).
 */
export type ScoutPopoverSessionContextValue = ScoutPopoverSession & {
  /** Current display state of the top-nav popover. */
  viewMode: ScoutPopoverViewMode;
  /** Resolved conversation title, mirrored from the store by the popover. */
  title: string | undefined;
  /** Show the popover (open state). */
  open: () => void;
  /** Drop the popover to its minimized bar, keeping the conversation. */
  minimize: () => void;
  /**
   * Hide the popover without clearing the conversation. Used when the user
   * lands on the full Scout page (the popover would be redundant there) and
   * for representative handoff. The conversation pointer is retained so the
   * page can offer a "resume conversation" chip.
   */
  close: () => void;
  /**
   * Seed a conversation into the popover and open it. Used when clicking an
   * in-app link from the full Scout page so Scout follows the user with the
   * same chat.
   */
  handoff: (conversationId: string, title?: string) => void;
};

// Safe no-op default so consumers rendered outside the provider (e.g. the
// Staff Observe surface, which reuses the chat message components) don't throw.
// Links there still navigate in-app; the handoff simply does nothing.
const NOOP_SESSION: ScoutPopoverSessionContextValue = {
  conversationId: undefined,
  setConversationId: () => undefined,
  openedAt: new Date(0),
  reset: () => undefined,
  newChat: () => undefined,
  setTitle: () => undefined,
  viewMode: "closed",
  title: undefined,
  open: () => undefined,
  minimize: () => undefined,
  close: () => undefined,
  handoff: () => undefined,
};

const ScoutPopoverSessionContext = createContext<ScoutPopoverSessionContextValue>(NOOP_SESSION);

export function ScoutPopoverSessionProvider({ children }: PropsWithChildren) {
  const tenantId = useTenantId();

  // Read (and TTL-evict) the persisted session once at mount rather than
  // repeating the getItem + JSON.parse in each state initializer below.
  const [initialSession] = useState(() => readScoutPopoverSession(tenantId));

  const [conversationId, setConversationIdState] = useState<string | undefined>(
    () => initialSession?.conversationId
  );
  const [openedAt, setOpenedAt] = useState<Date>(() => initialSession?.openedAt ?? new Date());
  const [viewMode, setViewMode] = useState<ScoutPopoverViewMode>(
    () => initialSession?.viewMode ?? "closed"
  );
  const [title, setTitleState] = useState<string | undefined>(() => initialSession?.title);

  // Reload the session when the tenant segment of the route changes so a
  // staff/partner user switching orgs sees that tenant's session (usually
  // none) rather than a stale thread. Set during render per the React docs.
  const [lastTenantId, setLastTenantId] = useState(tenantId);
  if (lastTenantId !== tenantId) {
    setLastTenantId(tenantId);
    const restored = readScoutPopoverSession(tenantId);
    setConversationIdState(restored?.conversationId);
    setOpenedAt(restored?.openedAt ?? new Date());
    setViewMode(restored?.viewMode ?? "closed");
    setTitleState(restored?.title);
  }

  // Persist on any change so a refresh can rehydrate.
  useEffect(() => {
    writeScoutPopoverSession(tenantId, { conversationId, openedAt, viewMode, title });
  }, [tenantId, conversationId, openedAt, viewMode, title]);

  const setConversationId = useCallback((id: string | undefined) => {
    setConversationIdState(id);
  }, []);

  const setTitle = useCallback((next: string | undefined) => {
    setTitleState((prev) => (prev === next ? prev : next));
  }, []);

  const open = useCallback(() => setViewMode("open"), []);
  const minimize = useCallback(() => setViewMode("minimized"), []);
  const close = useCallback(() => setViewMode("closed"), []);

  const reset = useCallback(() => {
    setConversationIdState(undefined);
    setTitleState(undefined);
    setOpenedAt(new Date());
    setViewMode("closed");
  }, []);

  // Like reset, but keeps the popover open — starts a fresh thread in place.
  const newChat = useCallback(() => {
    setConversationIdState(undefined);
    setTitleState(undefined);
    setOpenedAt(new Date());
  }, []);

  const handoff = useCallback((nextConversationId: string, nextTitle?: string) => {
    setConversationIdState(nextConversationId);
    setTitleState(nextTitle);
    setOpenedAt(new Date());
    setViewMode("open");
  }, []);

  const value: ScoutPopoverSessionContextValue = {
    conversationId,
    setConversationId,
    openedAt,
    reset,
    newChat,
    setTitle,
    viewMode,
    title,
    open,
    minimize,
    close,
    handoff,
  };

  return (
    <ScoutPopoverSessionContext.Provider value={value}>
      {children}
    </ScoutPopoverSessionContext.Provider>
  );
}

export function useScoutPopoverSession(): ScoutPopoverSessionContextValue {
  return useContext(ScoutPopoverSessionContext);
}
